import { makeStyles } from '@material-ui/core/styles'
import React, { useMemo } from 'react'
import { troubleshootImageLibraryDoc } from 'app/docLinks'
import LearnMoreLink from 'core/components/LearnMoreLink'
import Button from 'core/elements/button'
import Modal from 'core/elements/modal/Modal'
import Text from 'core/elements/Text'
import type Theme from 'core/themes/model'
import FontAwesomeIcon from './FontAwesomeIcon'

export type ActionRequiredIssue = {
  key?: string
  title: string | React.ReactNode
  description: React.ReactNode
  action?: React.ReactNode
  priority?: number
  severity?: 'info' | 'warning' | 'error'
  source?: string
}

type Props = {
  issues: ActionRequiredIssue[]
  modalTitle: string
  modalIntroText: React.ReactNode
  maxWidth?: number
  trigger: (options: {
    open: () => void
    openWithEvent: (e: React.SyntheticEvent) => void
    disabled: boolean
    count: number
  }) => React.ReactNode
  showTroubleshootInfo?: boolean
}

/**
 * Component that renders a trigger element and shows a modal with action items
 * This can be used to display important actions or warnings that require user attention
 * Currently used to display action items for Image Library issues
 * @param issues - Array of issues to display in the modal
 * @param modalTitle - Title of the modal
 * @param modalIntroText - Introductory text to display above the issues
 * @param maxWidth - Maximum width of the modal (default: 600)
 * @param trigger - Function that returns the trigger element
 * @returns a Modal component with the trigger button for displaying action items when there are issues requiring attention
 */
const ActionRequiredModalTrigger = ({
  issues,
  modalTitle,
  modalIntroText,
  maxWidth = 600,
  trigger,
  showTroubleshootInfo = true,
}: Props) => {
  const classes = useStyles()
  const [open, setOpen] = React.useState(false)
  const disabled = useMemo(() => !issues?.length, [issues])

  const sortedIssues = React.useMemo(() => {
    if (!issues?.length) return []

    // If no issues have priority, return as-is
    const hasAnyPriority = issues.some((issue) => typeof issue?.priority === 'number')
    if (!hasAnyPriority) return issues

    // Sort by priority (lower number = higher priority)
    return [...issues].sort((a, b) => {
      const ap = typeof a?.priority === 'number' ? a.priority : Number.POSITIVE_INFINITY
      const bp = typeof b?.priority === 'number' ? b.priority : Number.POSITIVE_INFINITY
      return ap - bp
    })
  }, [issues])

  const openModal = React.useCallback(() => {
    if (disabled) return
    setOpen(true)
  }, [disabled])

  const openWithEvent = React.useCallback(
    (e: React.SyntheticEvent) => {
      e.stopPropagation()
      e.preventDefault()
      openModal()
    },
    [openModal],
  )

  return (
    <>
      {trigger({ open: openModal, openWithEvent, disabled, count: issues?.length || 0 })}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={modalTitle}
        panel="dialog"
        maxWidth={maxWidth}
        footer={
          <Button variant="primary" onClick={() => setOpen(false)}>
            Close
          </Button>
        }
      >
        <Text variant="body2">{modalIntroText}</Text>
        <div className={classes.container}>
          {sortedIssues?.map((issue, idx) => (
            <div
              className={classes.issueContainer}
              key={issue.key || (typeof issue.title === 'string' ? issue.title : String(idx))}
            >
              <Text variant="subtitle2">
                <FontAwesomeIcon size="md" className={classes.icon}>
                  exclamation-circle
                </FontAwesomeIcon>
                {issue.title}
              </Text>
              <div className={classes.description}>{issue.description}</div>
              <div className={classes.description}>{issue.action}</div>
            </div>
          ))}

          {showTroubleshootInfo && (
            <Text variant="body2">
              Read here for <LearnMoreLink url={troubleshootImageLibraryDoc} text="more info" /> on
              troubleshooting.
            </Text>
          )}
        </div>
      </Modal>
    </>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  container: {
    display: 'grid',
    gridAutoRows: 'auto',
    gap: theme.spacing(3),
  },
  issueContainer: {
    display: 'grid',
    gridAutoRows: 'auto',
    gap: theme.spacing(0.5),
  },
  icon: {
    color: theme.components.badge.error.color,
    marginRight: theme.spacing(1),
  },
  description: {
    marginLeft: theme.spacing(3),
  },
}))

export default ActionRequiredModalTrigger
